import { createFileRoute, useParams, useLocation } from '@tanstack/react-router';
import { useTheme, useMediaQuery } from '@mui/material';
import { WeatherForecastPage, useAuth } from '@amber-ui/core';

/**
 * Weather forecast screenshot route — used by headless Chromium (Lambda) to
 * capture grain bin weather forecast images for weekly emails.
 *
 * Uses useAuth() instead of useGetViewer() to avoid the auth timing issue where
 * useQuery(GetViewerDocument) fires before the RenewToken mutation's cache write
 * completes. The AuthProvider context is guaranteed to be populated by the time
 * child route components render (the _auth layout's beforeLoad passes through on
 * first resolution, then re-evaluates once AuthProvider signals ready=true).
 *
 * The component only uses viewer.user_id for a security check — the auth context
 * provides this directly.
 */
function WeatherForecastRoute() {
  const { user_id, account_id, grain_bin_id } = useParams({
    from: '/_auth/_grain-bin/grain_bin/weather_forecast/$user_id/$account_id/$grain_bin_id',
  });
  const { pathname } = useLocation();
  const theme = useTheme();
  const mobile_width = useMediaQuery(theme.breakpoints.down('sm'));
  const { user_id: auth_user_id, ready } = useAuth();

  if (!ready || !auth_user_id) return null;

  const viewer = { user_id: auth_user_id };

  return (
    <WeatherForecastPage
      grain_bin_id={grain_bin_id}
      account_id={account_id}
      mobile_width={mobile_width}
      pathname={pathname}
      viewer={viewer}
      routeParams={{ user_id, account_id, grain_bin_id }}
    />
  );
}

export const Route = createFileRoute(
  '/_auth/_grain-bin/grain_bin/weather_forecast/$user_id/$account_id/$grain_bin_id'
)({
  component: WeatherForecastRoute,
});
